const RATE_LIMIT_KEY = 'rateLimitedRpcs';
const RATE_LIMIT_COOLDOWN_MS = 1000;

type WeightedRpc = {
    url: string;
    weight: number;
};

function saveRateLimitsToStorage(rateLimits: Record<string, number>) {
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(rateLimits));
}

function loadRateLimitsFromStorage(): Record<string, number> {
    try {
        const data = localStorage.getItem(RATE_LIMIT_KEY);
        if (!data) return {};
        const obj = JSON.parse(data);
        if (typeof obj !== 'object' || obj === null) return {};
        return obj;
    } catch {
        return {};
    }
}

const rpcRateLimitUntil: Record<string, number> = loadRateLimitsFromStorage();

function cleanupRateLimits() {
    const now = Date.now();
    let changed = false;
    Object.keys(rpcRateLimitUntil).forEach((url) => {
        if (rpcRateLimitUntil[url] < now) {
            delete rpcRateLimitUntil[url];
            changed = true;
        }
    });
    if (changed) {
        saveRateLimitsToStorage(rpcRateLimitUntil);
    }
}

export function markRpcRateLimited(url: string) {
    rpcRateLimitUntil[url] = Date.now() + RATE_LIMIT_COOLDOWN_MS;
    saveRateLimitsToStorage(rpcRateLimitUntil);
}

function isRpcBlocked(url: string) {
    cleanupRateLimits();
    return rpcRateLimitUntil[url] && rpcRateLimitUntil[url] > Date.now();
}

export const parseWeightedRpcUrls = (envVar: string): WeightedRpc[] => {
    if (!envVar || typeof envVar !== 'string') {
        return [];
    }

    return envVar.split(',').map((entry) => {
        const trimmedEntry = entry.trim();

        if (trimmedEntry.match(/^https?:\/\/[^:]+$/)) {
            return { url: trimmedEntry, weight: 1 };
        }

        const lastColonIndex = trimmedEntry.lastIndexOf(':');
        const url = trimmedEntry.slice(0, lastColonIndex).trim();
        const weight = Number(trimmedEntry.slice(lastColonIndex + 1).trim());

        return {
            url,
            weight: isNaN(weight) || weight < 1 ? 1 : weight,
        };
    });
};

export function getRandomRpcUrlByWeight(weightedRpcs: WeightedRpc[]): string {
    cleanupRateLimits();

    const available = weightedRpcs.filter((rpc) => !isRpcBlocked(rpc.url));
    const rpcsToUse = available.length > 0 ? available : weightedRpcs;

    const pool: string[] = [];
    rpcsToUse.forEach(({ url, weight }) => {
        for (let i = 0; i < weight; i++) {
            pool.push(url);
        }
    });

    if (pool.length === 0) {
        throw new Error('No RPC URLs available');
    }

    const randomIndex = Math.floor(Math.random() * pool.length);
    return pool[randomIndex];
}
